import { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { checkIsAdmin } from '@/lib/admin-utils';
import { motion } from 'framer-motion';
import {
  Shield,
  Users,
  QrCode,
  DollarSign,
  Activity,
  TrendingUp,
  Download,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Ban,
  CheckCircle,
  XCircle,
  Clock,
  Zap,
  Globe,
  Mail,
  CreditCard,
  AlertTriangle,
  ShieldAlert,
  MessageSquare,
} from 'lucide-react';
import { Layout } from '@/components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useAuthStore } from '@/stores/authStore';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { SecurityModerationTable } from '@/components/security/SecurityModerationTable';
import { AdminSupportChat } from '@/components/security/AdminSupportChat';
import BlogManagement from '@/pages/admin/BlogManagement';
import NewsletterManagement from '@/pages/admin/NewsletterManagement';

interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalQRCodes: number;
  totalScans: number;
  totalRevenue: number;
  monthlyRevenue: number;
  paidUsers: number;
  freeUsers: number;
}

interface User {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string;
  subscription_tier?: string;
  total_qr_codes?: number;
  total_scans?: number;
}

export default function Admin() {
  const { user, isLoading: authLoading } = useAuthStore();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<DashboardStats>({
    totalUsers: 0,
    activeUsers: 0,
    totalQRCodes: 0,
    totalScans: 0,
    totalRevenue: 0,
    monthlyRevenue: 0,
    paidUsers: 0,
    freeUsers: 0,
  });
  const [users, setUsers] = useState<User[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    if (!authLoading) {
      if (user) {
        checkAdminAccess();
      } else {
        setLoading(false);
      }
    }
  }, [user, authLoading]);

  const checkAdminAccess = async () => {
    try {
      const isAdminUser = await checkIsAdmin(user!.id);
      
      setIsAdmin(isAdminUser);
      
      if (isAdminUser) {
        await fetchDashboardData();
      }
    } catch (error) {
      console.error('Admin check error:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      // Fetch users count
      const { count: totalUsersCount, error: usersError } = await supabase
        .from('profiles')
        .select('*', { count: 'exact', head: true });

      if (usersError) {
        console.error('Error fetching users count:', usersError);
      }

      // Fetch active users - use created_at as fallback since last_sign_in_at doesn't exist in profiles
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const { data: activeUsersData, error: activeError } = await supabase
        .from('profiles')
        .select('id')
        .gte('created_at', thirtyDaysAgo.toISOString());

      if (activeError) {
        console.error('Error fetching active users:', activeError);
      }

      // Fetch QR codes count
      const { count: qrCodesCount, error: qrError } = await supabase
        .from('qr_codes')
        .select('*', { count: 'exact', head: true });

      if (qrError) {
        console.error('Error fetching QR codes:', qrError);
      }

      // Fetch total scans
      const { count: scansCount, error: scansError } = await supabase
        .from('qr_scans')
        .select('*', { count: 'exact', head: true });

      if (scansError) {
        console.error('Error fetching scans:', scansError);
      }

      // Fetch subscription data
      const { data: subscriptions, error: subError } = await supabase
        .from('subscriptions')
        .select('plan, status')
        .eq('status', 'active');

      if (subError) {
        console.error('Error fetching subscriptions:', subError);
      }

      const paidUsers = subscriptions?.filter(s => 
        s.plan !== 'free' && s.status === 'active'
      ).length || 0;

      const freeUsers = (totalUsersCount || 0) - paidUsers;

      // Calculate revenue (mock data - replace with actual payment data)
      const planPrices: Record<string, number> = {
        starter: 9,
        professional: 29,
        business: 99,
        enterprise: 299,
      };

      const totalRevenue = subscriptions?.reduce((sum, sub) => {
        return sum + (planPrices[sub.plan] || 0);
      }, 0) || 0;

      const monthlyRevenue = totalRevenue;

      console.log('Dashboard stats:', {
        totalUsers: totalUsersCount,
        activeUsers: activeUsersData?.length,
        qrCodes: qrCodesCount,
        scans: scansCount,
        paid: paidUsers,
        free: freeUsers,
      });

      setStats({
        totalUsers: totalUsersCount || 0,
        activeUsers: activeUsersData?.length || 0,
        totalQRCodes: qrCodesCount || 0,
        totalScans: scansCount || 0,
        totalRevenue,
        monthlyRevenue,
        paidUsers,
        freeUsers,
      });

      // Fetch user details
      await fetchUsers();
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive',
      });
    }
  };

  const fetchUsers = async () => {
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select(`
          id,
          email,
          created_at
        `)
        .order('created_at', { ascending: false })
        .limit(100);

      if (error) throw error;

      // Fetch QR code counts for each user
      const usersWithStats = await Promise.all(
        (data || []).map(async (profile) => {
          const { count: qrCount } = await supabase
            .from('qr_codes')
            .select('*', { count: 'exact', head: true })
            .eq('user_id', profile.id);

          const { data: userQRs } = await supabase
            .from('qr_codes')
            .select('id')
            .eq('user_id', profile.id);

          const qrIds = userQRs?.map(qr => qr.id) || [];
          
          let totalScans = 0;
          if (qrIds.length > 0) {
            const { count: scanCount } = await supabase
              .from('qr_scans')
              .select('*', { count: 'exact', head: true })
              .in('qr_code_id', qrIds);
            totalScans = scanCount || 0;
          }

          // Get subscription
          const { data: sub } = await supabase
            .from('subscriptions')
            .select('plan')
            .eq('user_id', profile.id)
            .single();

          return {
            ...profile,
            subscription_tier: sub?.plan || 'free',
            total_qr_codes: qrCount || 0,
            total_scans: totalScans,
            last_sign_in_at: profile.created_at, // Using created_at as fallback
          };
        })
      );

      setUsers(usersWithStats);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const handleBanUser = async (userId: string) => {
    try {
      // Implement ban logic via Supabase Admin API
      toast({
        title: 'User Banned',
        description: 'User has been banned successfully',
      });
      await fetchUsers();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to ban user',
        variant: 'destructive',
      });
    }
  };

  const exportData = (type: string) => {
    toast({
      title: 'Export Started',
      description: `Exporting ${type} data...`,
    });
    // Implement export logic
  };

  if (authLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!user) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
          <Shield className="h-16 w-16 text-muted-foreground mb-4" />
          <h2 className="text-2xl font-bold mb-2">Admin Access Required</h2>
          <p className="text-muted-foreground mb-6">
            Please log in with an admin account to access this page.
          </p>
          <Button onClick={() => window.location.href = '/auth'}>Go to Login</Button>
        </div>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      </Layout>
    );
  }

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const filteredUsers = users.filter(u =>
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8 max-w-7xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Shield className="h-8 w-8 text-primary" />
                Admin Dashboard
              </h1>
              <p className="text-muted-foreground mt-1">
                Platform analytics and management
              </p>
            </div>
            <Badge variant="destructive" className="h-7">
              Admin Access
            </Badge>
          </div>
        </motion.div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total Users
                  </CardTitle>
                  <Users className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalUsers.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  <span className="text-green-600">+{stats.activeUsers}</span> active (30d)
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    QR Codes Created
                  </CardTitle>
                  <QrCode className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalQRCodes.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {(stats.totalQRCodes / stats.totalUsers).toFixed(1)} avg per user
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Total Scans
                  </CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalScans.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {(stats.totalScans / stats.totalQRCodes).toFixed(1)} avg per QR
                </p>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Monthly Revenue
                  </CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">${stats.monthlyRevenue.toLocaleString()}</div>
                <p className="text-xs text-muted-foreground mt-1">
                  {stats.paidUsers} paid • {stats.freeUsers} free
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Main Content Tabs */}
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="flex flex-wrap h-auto p-1.5 bg-muted/50 rounded-lg gap-1 w-full">
            <TabsTrigger value="users" className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              <span className="hidden sm:inline">Users</span>
            </TabsTrigger>
            <TabsTrigger value="qrcodes" className="flex items-center gap-2">
              <QrCode className="h-4 w-4" />
              <span className="hidden sm:inline">QR Codes</span>
            </TabsTrigger>
            <TabsTrigger value="payments" className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              <span className="hidden sm:inline">Payments</span>
            </TabsTrigger>
            <TabsTrigger value="analytics" className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              <span className="hidden sm:inline">Analytics</span>
            </TabsTrigger>
            <TabsTrigger value="blogs" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Blogs</span>
            </TabsTrigger>
            <TabsTrigger value="newsletter" className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              <span className="hidden sm:inline">Newsletter</span>
            </TabsTrigger>
            <TabsTrigger value="security" className="flex items-center gap-2">
              <ShieldAlert className="h-4 w-4" />
              <span className="hidden sm:inline">Security</span>
            </TabsTrigger>
            <TabsTrigger value="support" className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" />
              <span className="hidden sm:inline">Support</span>
            </TabsTrigger>
          </TabsList>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle>User Management</CardTitle>
                    <CardDescription>View and manage all platform users</CardDescription>
                  </div>
                  <Button onClick={() => exportData('users')} variant="outline" size="sm">
                    <Download className="h-4 w-4 mr-2" />
                    Export
                  </Button>
                </div>
                <div className="flex items-center gap-4 mt-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search users by email..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Joined</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>QR Codes</TableHead>
                      <TableHead>Scans</TableHead>
                      <TableHead>Last Active</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell className="font-medium">{user.email}</TableCell>
                        <TableCell>{format(new Date(user.created_at), 'MMM d, yyyy')}</TableCell>
                        <TableCell>
                          <Badge variant={user.subscription_tier === 'free' ? 'secondary' : 'default'}>
                            {user.subscription_tier || 'Free'}
                          </Badge>
                        </TableCell>
                        <TableCell>{user.total_qr_codes || 0}</TableCell>
                        <TableCell>{user.total_scans || 0}</TableCell>
                        <TableCell>
                          {user.last_sign_in_at
                            ? format(new Date(user.last_sign_in_at), 'MMM d')
                            : 'Never'}
                        </TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem>
                                <Eye className="h-4 w-4 mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleBanUser(user.id)} className="text-red-600">
                                <Ban className="h-4 w-4 mr-2" />
                                Ban User
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* QR Codes Tab */}
          <TabsContent value="qrcodes">
            <Card>
              <CardHeader>
                <CardTitle>QR Code Analytics</CardTitle>
                <CardDescription>Platform-wide QR code statistics and insights</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Most Popular Type</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold">URL</p>
                      <p className="text-sm text-muted-foreground">45% of all codes</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Dynamic QRs</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold">
                        {Math.floor(stats.totalQRCodes * 0.65).toLocaleString()}
                      </p>
                      <p className="text-sm text-muted-foreground">65% conversion rate</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-sm">Avg Scans/QR</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-2xl font-bold">
                        {(stats.totalScans / stats.totalQRCodes).toFixed(1)}
                      </p>
                      <p className="text-sm text-muted-foreground">Last 30 days</p>
                    </CardContent>
                  </Card>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Payments Tab */}
          <TabsContent value="payments">
            <Card>
              <CardHeader>
                <CardTitle>Payment Overview</CardTitle>
                <CardDescription>Revenue and subscription analytics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Total Revenue</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-3xl font-bold text-green-600">
                          ${stats.totalRevenue.toLocaleString()}
                        </p>
                        <p className="text-sm text-muted-foreground mt-1">All time</p>
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-sm">Subscription Split</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-sm">Paid Users</span>
                            <span className="font-medium">{stats.paidUsers}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-sm">Free Users</span>
                            <span className="font-medium">{stats.freeUsers}</span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Analytics Tab */}
          <TabsContent value="analytics">
            <Card>
              <CardHeader>
                <CardTitle>Platform Analytics</CardTitle>
                <CardDescription>Usage trends and performance metrics</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Zap className="h-4 w-4 text-yellow-600" />
                        <span className="text-sm font-medium">Peak Hours</span>
                      </div>
                      <p className="text-2xl font-bold">2PM - 5PM</p>
                      <p className="text-xs text-muted-foreground">Highest activity</p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Globe className="h-4 w-4 text-blue-600" />
                        <span className="text-sm font-medium">Top Region</span>
                      </div>
                      <p className="text-2xl font-bold">North America</p>
                      <p className="text-xs text-muted-foreground">52% of scans</p>
                    </div>
                    <div className="p-4 border rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <Mail className="h-4 w-4 text-green-600" />
                        <span className="text-sm font-medium">Email Open Rate</span>
                      </div>
                      <p className="text-2xl font-bold">68%</p>
                      <p className="text-xs text-muted-foreground">Above industry avg</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blog Management Tab */}
          <TabsContent value="blogs">
            <BlogManagement />
          </TabsContent>

          {/* Newsletter Tab */}
          <TabsContent value="newsletter">
            <NewsletterManagement />
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card>
              <CardHeader>
                <CardTitle>Security Moderation</CardTitle>
                <CardDescription>Review and manage flagged QR codes</CardDescription>
              </CardHeader>
              <CardContent>
                <SecurityModerationTable />
              </CardContent>
            </Card>
          </TabsContent>

          {/* Support Tab */}
          <TabsContent value="support">
            <AdminSupportChat />
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
